/**
 * Routine editor dialog (REQ-FRT-001/002/004; remediación ISS-641 y revisión
 * UI del 18-Oct-2026).
 *
 * Creates a routine in `draft`, edits its configuration (agent, prompt,
 * parameter schema, objective, completion criteria, execution identity and
 * schedule) and upserts the schedule trigger. The backend validates the
 * frontier profile, the template, the identity permissions and the canonical
 * schedule, surfacing domain errors (FRONTIER_AGENT_NOT_AUTONOMOUS,
 * TEMPLATE_VARIABLE_UNKNOWN, RUN_AS_USER_FORBIDDEN, INPUT_INVALID, …).
 */

import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { useAuth } from '@/lib/auth';
import {
  useCreateRoutine,
  useRotateTrigger,
  useRoutineConfig,
  useUpdateRoutine,
  useUpsertTrigger,
} from '../hooks/useFrontier';
import { AgentHiveSelect } from './AgentHiveSelect';
import { IdentitySelect, type RunAsValue } from './IdentitySelect';
import {
  ScheduleBuilder,
  defaultScheduleState,
  scheduleStateToSpec,
  specToScheduleState,
  type ScheduleBuilderState,
} from './ScheduleBuilder';
import type { RoutineCreateInput, RoutineTriggerSummary } from '../types';

function errorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const candidate = detail as { message?: string; code?: string };
    if (candidate.message) return candidate.message;
    if (candidate.code) return candidate.code;
  }
  return fallback;
}

export interface RoutineEditorDialogProps {
  open: boolean;
  routineId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function RoutineEditorDialog({
  open,
  routineId,
  onOpenChange,
}: RoutineEditorDialogProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const editing = !!routineId;
  const create = useCreateRoutine();
  const update = useUpdateRoutine();
  const upsertTrigger = useUpsertTrigger();
  const config = useRoutineConfig(open && routineId ? routineId : null);

  const [name, setName] = useState('');
  const [agentId, setAgentId] = useState('');
  const [objective, setObjective] = useState('');
  const [criteria, setCriteria] = useState('');
  const [prompt, setPrompt] = useState('');
  const [schema, setSchema] = useState('[]');
  const [runAs, setRunAs] = useState<RunAsValue>({ mode: 'user', subject_id: '' });
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [schedule, setSchedule] = useState<ScheduleBuilderState>(() =>
    defaultScheduleState(),
  );
  const [error, setError] = useState<string | null>(null);
  const rotate = useRotateTrigger();
  const [reveal, setReveal] = useState<{ url: string; secret: string } | null>(null);
  const triggers = config.data?.triggers ?? [];
  const webhookTrigger = triggers.find((trigger: RoutineTriggerSummary) => trigger.type === 'webhook');
  const apiTrigger = triggers.find((trigger: RoutineTriggerSummary) => trigger.type === 'api');

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (routineId && config.data) {
      const current = config.data;
      setName(current.name);
      setAgentId(current.agent_id);
      setObjective(current.objective);
      setCriteria((current.completion_criteria ?? []).join('\n'));
      setPrompt(current.start_prompt_template);
      setSchema(JSON.stringify(current.input_schema ?? [], null, 2));
      setRunAs({
        mode: current.run_as.mode,
        subject_id: current.run_as.subject_id,
      });
      if (current.schedule) {
        setSchedule(specToScheduleState(current.schedule));
        setScheduleEnabled(current.schedule_enabled ?? false);
      } else {
        setSchedule(defaultScheduleState());
        setScheduleEnabled(false);
      }
    } else if (!routineId) {
      setName('');
      setAgentId('');
      setObjective('');
      setCriteria('');
      setPrompt('');
      setSchema('[]');
      setRunAs({ mode: 'user', subject_id: user?.id ?? '' });
      setScheduleEnabled(true);
      setSchedule(defaultScheduleState());
    }
  }, [open, routineId, config.data, user?.id]);

  const pending = create.isPending || update.isPending || upsertTrigger.isPending;

  const handleSave = async () => {
    setError(null);
    if (!name.trim() || !agentId.trim() || !objective.trim() || !prompt.trim()) {
      setError(t('frontier.editor.required'));
      return;
    }
    if (!runAs.subject_id.trim()) {
      setError(t('frontier.editor.requiredRunAs'));
      return;
    }
    let inputSchema: unknown;
    try {
      inputSchema = JSON.parse(schema || '[]');
    } catch {
      setError(t('frontier.editor.invalidSchema'));
      return;
    }
    if (!Array.isArray(inputSchema)) {
      setError(t('frontier.editor.invalidSchema'));
      return;
    }

    const triggerSpec = scheduleStateToSpec(schedule);
    if (scheduleEnabled && !triggerSpec) {
      setError(t('frontier.editor.scheduleInvalid'));
      return;
    }

    const payload: RoutineCreateInput = {
      name: name.trim(),
      agent_id: agentId.trim(),
      start_prompt_template: prompt,
      input_schema: inputSchema as RoutineCreateInput['input_schema'],
      objective: objective.trim(),
      completion_criteria: criteria
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
      run_as: { mode: runAs.mode, subject_id: runAs.subject_id.trim() },
      limits: {},
      approval_mode: 'pause_and_notify',
      input_request_policy: 'auto_continue',
      overlap_policy: 'skip',
      tags: [],
    };

    try {
      let id = routineId;
      if (id) {
        await update.mutateAsync({ routineId: id, payload });
      } else {
        const created = await create.mutateAsync(payload);
        id = created.id;
      }
      if (scheduleEnabled && triggerSpec) {
        await upsertTrigger.mutateAsync({
          routineId: id,
          payload: { type: 'schedule', enabled: true, schedule: triggerSpec },
        });
      }
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, t('frontier.editor.saveError')));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? t('frontier.editor.editTitle')
              : t('frontier.editor.createTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="routine-name">{t('frontier.editor.name')}</Label>
            <Input
              id="routine-name"
              data-testid="routine-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('frontier.editor.agent')}</Label>
            <AgentHiveSelect
              value={agentId || null}
              onChange={(id) => setAgentId(id)}
            />
            <p className="text-xs text-muted-foreground">
              {t('frontier.editor.agentHint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="routine-objective">{t('frontier.editor.objective')}</Label>
            <Input
              id="routine-objective"
              data-testid="routine-objective"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="routine-criteria">{t('frontier.editor.criteria')}</Label>
            <Textarea
              id="routine-criteria"
              data-testid="routine-criteria"
              rows={3}
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="routine-prompt">{t('frontier.editor.prompt')}</Label>
            <Textarea
              id="routine-prompt"
              data-testid="routine-prompt"
              rows={6}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Analiza {{param.region}} …"
            />
            <p className="text-xs text-muted-foreground">
              {t('frontier.editor.promptHint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="routine-schema">{t('frontier.editor.schema')}</Label>
            <Textarea
              id="routine-schema"
              data-testid="routine-schema"
              rows={4}
              value={schema}
              onChange={(e) => setSchema(e.target.value)}
              className="font-mono text-xs"
            />
            <div
              data-testid="param-schema-help"
              className="space-y-1 rounded-lg border bg-muted/30 p-3 text-xs"
            >
              <p className="font-medium">{t('frontier.editor.schemaHelpTitle')}</p>
              <p>{t('frontier.editor.schemaHelpIntro')}</p>
              <ul className="list-disc space-y-0.5 pl-4">
                <li>{t('frontier.editor.schemaHelpFields')}</li>
                <li>{t('frontier.editor.schemaHelpTypes')}</li>
                <li>{t('frontier.editor.schemaHelpUsage')}</li>
                <li>{t('frontier.editor.schemaHelpOverridable')}</li>
              </ul>
              <p className="text-muted-foreground">{t('frontier.editor.schemaHelpExample')}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('frontier.editor.schemaHint')}
            </p>
          </div>

          <fieldset className="space-y-3 rounded-lg border p-4">
            <legend className="px-1 text-sm font-medium">
              {t('frontier.editor.identity')}
            </legend>
            <IdentitySelect value={runAs} onChange={setRunAs} />
          </fieldset>

          <fieldset className="space-y-3 rounded-lg border p-4">
            <legend className="px-1 text-sm font-medium">
              {t('frontier.editor.schedule')}
            </legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="routine-schedule-enabled"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
              />
              {t('frontier.editor.scheduleEnabled')}
            </label>
            {editing && !scheduleEnabled && (
              <p className="text-xs text-muted-foreground">
                {t('frontier.editor.scheduleUpdateHint')}
              </p>
            )}
            {scheduleEnabled && (
              <ScheduleBuilder value={schedule} onChange={setSchedule} />
            )}
          </fieldset>

        {editing && (
          <fieldset className="space-y-3 rounded-lg border p-4" data-testid="trigger-provisioning">
            <legend className="px-1 text-sm font-medium">{t('frontier.triggers.title')}</legend>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('frontier.triggers.existing')}:
              </span>
              {triggers.length === 0 && <span>{t('frontier.triggers.none')}</span>}
              {triggers.map((trigger: RoutineTriggerSummary) => (
                <span key={trigger.id} className="rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-800">
                  {trigger.type}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {!webhookTrigger && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="webhook-create"
                  disabled={upsertTrigger.isPending}
                  onClick={async () => {
                    try {
                      const created = await upsertTrigger.mutateAsync({
                        routineId: routineId as string,
                        payload: { type: 'webhook', enabled: true },
                      });
                      const token = (created as { path_token?: string }).path_token ?? '';
                      const secret = (created as { signing_secret?: string }).signing_secret ?? '';
                      const triggerId = (created as { id?: string }).id ?? '';
                      setReveal({
                        url: `https://${window.location.host}/webhooks/frontier/${triggerId}/${token}`,
                        secret,
                      });
                    } catch (err) {
                      setError(errorMessage(err, t('frontier.editor.saveError')));
                    }
                  }}
                >
                  {t('frontier.triggers.webhookCreate')}
                </Button>
              )}
              {webhookTrigger && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="webhook-rotate"
                  disabled={rotate.isPending}
                  onClick={async () => {
                    try {
                      const rotated = await rotate.mutateAsync({
                        routineId: routineId as string,
                        triggerId: webhookTrigger.id,
                      });
                      setReveal({
                        url: `https://${window.location.host}/webhooks/frontier/${rotated.id}/${rotated.path_token ?? ''}`,
                        secret: rotated.signing_secret ?? '',
                      });
                    } catch (err) {
                      setError(errorMessage(err, t('frontier.editor.saveError')));
                    }
                  }}
                >
                  {t('frontier.triggers.rotate')}
                </Button>
              )}
              {!apiTrigger && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="api-enable"
                  disabled={upsertTrigger.isPending}
                  onClick={async () => {
                    try {
                      await upsertTrigger.mutateAsync({
                        routineId: routineId as string,
                        payload: { type: 'api', enabled: true },
                      });
                    } catch (err) {
                      setError(errorMessage(err, t('frontier.editor.saveError')));
                    }
                  }}
                >
                  {t('frontier.triggers.apiEnable')}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t('frontier.triggers.apiHint')}</p>
            {reveal && (
              <div
                data-testid="webhook-secret-reveal"
                className="space-y-1 rounded-lg bg-amber-50 p-3 text-xs dark:bg-amber-950"
              >
                <p className="font-medium">{t('frontier.triggers.revealWarning')}</p>
                <p>
                  {t('frontier.triggers.webhookUrl')}: <code className="break-all">{reveal.url}</code>
                </p>
                <p>
                  {t('frontier.triggers.signingSecret')}:{' '}
                  <code className="break-all">{reveal.secret}</code>
                </p>
              </div>
            )}
          </fieldset>
        )}
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t('frontier.editor.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={pending}
            data-testid="routine-save"
          >
            {pending ? t('frontier.editor.saving') : t('frontier.editor.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
